/**
 * Which account this page runs as, and a one-shot mark for an account switch
 * THIS tab is making on purpose.
 *
 * The Firebase session is shared by every tab of a browser profile, so a
 * sign-in in another tab makes `auth.currentUser` a different person under a
 * page that is still showing, caching and editing for the first. AuthContext
 * reloads such a tab (`isAccountSwitch`), but a reload can be refused (a
 * "Leave site?" prompt over unsaved work) and runs only after the new
 * account's hydration. Until the page is gone, nothing may fetch a token for
 * the new account: every reader that takes a token from `auth.currentUser`
 * asks `mayUseAccount` first, and a refusal surfaces as the request's own
 * error instead of a silent change of identity.
 *
 * A page that swaps accounts itself (a phone redeeming a second magic link)
 * finishes with its own full load to its own destination, and a reload of the
 * current URL racing it would land the person back where they started. It
 * marks the switch before signing in; the next replaced account is then left
 * to it.
 */

let pagePrincipal: string | null = null;
let intended = false;
let reloadAskedFor: string | null = null;

/** The account this page runs as; null while signed out or before the first one. */
export function setPagePrincipal(uid: string | null): void {
  pagePrincipal = uid;
  reloadAskedFor = null;
}

/**
 * Whether to reload for a switch to `uid`: once per switch. Both auth
 * listeners report it, and the new account's token keeps rotating; a person
 * who answered "Stay" to a "Leave site?" prompt over unsaved work is not asked
 * again for the same switch (its readers keep refusing it regardless).
 */
export function shouldReloadForAccount(uid: string): boolean {
  if (reloadAskedFor === uid) return false;
  reloadAskedFor = uid;
  return true;
}

/** Whether a token for `uid` may be used by this page. */
export function mayUseAccount(uid: string): boolean {
  return pagePrincipal === null || pagePrincipal === uid;
}

/**
 * `user` if this page may act as them, else null: the other account reads as
 * no user at all, so each caller's own signed-out handling applies.
 */
export function pageUser<T extends { uid: string }>(user: T | null): T | null {
  return user && mayUseAccount(user.uid) ? user : null;
}

export function markIntendedAccountSwitch(): void {
  intended = true;
}

/** Withdraws a mark whose sign-in failed, so a later real switch still reloads. */
export function clearIntendedAccountSwitch(): void {
  intended = false;
}

/** Whether a switch is marked, without using up the mark. */
export function isIntendedAccountSwitch(): boolean {
  return intended;
}

/** Whether a switch was marked, clearing the mark: it covers one switch. */
export function consumeIntendedAccountSwitch(): boolean {
  const was = intended;
  intended = false;
  return was;
}
